import { IconCheck, IconCoins } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { VideoResolution } from '@/ai/video-models';
import { trackRetentionEvent } from '@/analytics/retention-events';
import { authClient } from '@/auth/client';
import { CreditCheckoutButton } from '@/components/settings/credits/credit-checkout-button';
import {
  getPricingPlanGroups,
  type PricingPlan,
} from '@/config/pricing-config';
import { getTrendCredits, TRENDS } from '@/config/trends';
import { getCreditPackageById } from '@/credits/packages';
import { Routes } from '@/lib/routes';
import { cn } from '@/lib/utils';

/** Every pack is priced against the homepage effect at 480p. */
const ANCHOR_TREND = TRENDS['crazy-frog'];
const ANCHOR_VIDEO_CREDITS = getTrendCredits(
  ANCHOR_TREND,
  VideoResolution.P480
);

function formatUsdPrice(price: number) {
  return price.toLocaleString('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
  });
}

export function OneTimePricingCards({
  checkoutMetadata,
  className,
  compact = false,
  onBeforeCheckout,
  recommendedId,
  returnTo,
  returnUrl,
  source = 'pricing_page',
}: {
  checkoutMetadata?: Record<string, string>;
  className?: string;
  compact?: boolean;
  onBeforeCheckout?: () => void | Promise<void>;
  /** Highlights this pack instead of the "Most popular" one. */
  recommendedId?: string;
  returnTo?: string;
  returnUrl?: string;
  source?: string;
}) {
  const { data: session } = authClient.useSession();
  const isLoggedIn = Boolean(session?.user?.id);
  const trackedViewRef = useRef(false);
  const plans = getPricingPlanGroups().oneTime;

  useEffect(() => {
    if (trackedViewRef.current) return;
    trackedViewRef.current = true;
    trackRetentionEvent('pricing_view', {
      compact,
      pricingMode: 'one-time',
      source: checkoutMetadata?.source ?? source,
    });
  }, [checkoutMetadata?.source, compact, source]);

  return (
    <div
      data-testid="pricing-packages"
      className={cn(
        'grid grid-cols-1 items-stretch md:grid-cols-3',
        compact ? 'gap-3' : 'gap-5',
        className
      )}
    >
      {plans.map((plan) => (
        <OneTimePricingCard
          key={plan.id}
          checkoutMetadata={checkoutMetadata}
          compact={compact}
          highlight={
            recommendedId
              ? plan.id === recommendedId
                ? 'Recommended'
                : undefined
              : plan.isPopular
                ? 'Most popular'
                : undefined
          }
          isLoggedIn={isLoggedIn}
          onBeforeCheckout={onBeforeCheckout}
          plan={plan}
          returnTo={returnTo}
          returnUrl={returnUrl}
        />
      ))}
    </div>
  );
}

function OneTimePricingCard({
  checkoutMetadata,
  compact,
  highlight,
  isLoggedIn,
  onBeforeCheckout,
  plan,
  returnTo,
  returnUrl,
}: {
  checkoutMetadata?: Record<string, string>;
  compact: boolean;
  highlight?: string;
  isLoggedIn: boolean;
  onBeforeCheckout?: () => void | Promise<void>;
  plan: PricingPlan;
  returnTo?: string;
  returnUrl?: string;
}) {
  const creditPackage = getCreditPackageById(plan.packageId ?? plan.id);
  const canCheckout = Boolean(creditPackage?.price.priceId);
  const videos = Math.floor(plan.creditAmount / ANCHOR_VIDEO_CREDITS);
  const actionLabel = isLoggedIn
    ? `Buy ${plan.creditAmount} credits`
    : 'Log in to buy';
  const buttonClassName = cn(
    'flex h-11 w-full items-center justify-center gap-2 rounded-xl px-3 font-semibold text-sm transition',
    highlight
      ? 'bg-[#8ef25a] text-[#071107] hover:bg-[#a6ff73]'
      : 'border border-white/15 bg-white/[0.06] text-white hover:border-[#8ef25a]/60 hover:bg-white/[0.09]'
  );

  return (
    <article
      data-testid={`pricing-plan-${plan.id}`}
      className={cn(
        'relative flex h-full flex-col rounded-2xl border bg-[#121212]',
        compact ? 'p-4' : 'p-6',
        highlight
          ? 'border-[#8ef25a] shadow-[0_0_40px_rgba(142,242,90,0.12)]'
          : 'border-white/10'
      )}
    >
      {highlight ? (
        <span className="-top-3 absolute left-5 rounded-full bg-[#8ef25a] px-2.5 py-0.5 font-semibold text-[#071107] text-[11px]">
          {highlight}
        </span>
      ) : null}
      <p className="font-semibold text-white/85">{plan.name}</p>
      <p
        className={cn(
          'font-bold text-white tracking-tight',
          compact ? 'mt-2 text-3xl' : 'mt-4 text-5xl'
        )}
      >
        ${formatUsdPrice(plan.price)}
      </p>
      <p className="mt-2 font-semibold text-[#8ef25a]">
        {plan.creditAmount} credits
      </p>
      <p className="mt-1 text-sm text-white/60">
        {videos === 1
          ? `1 ${ANCHOR_TREND.name} video at 480p`
          : `${videos} ${ANCHOR_TREND.name} videos at 480p`}
      </p>
      {compact ? null : (
        <ul className="mt-6 flex flex-1 flex-col gap-3">
          {plan.features.map((feature) => (
            <li key={feature.text} className="flex items-start gap-2.5">
              <IconCheck className="mt-0.5 size-4 shrink-0 text-[#8ef25a]" />
              <span
                className={cn(
                  'text-sm leading-5',
                  feature.emphasis ? 'text-white' : 'text-white/70'
                )}
              >
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className={compact ? 'mt-4' : 'mt-8'}>
        {isLoggedIn && creditPackage && canCheckout ? (
          <CreditCheckoutButton
            className={buttonClassName}
            metadata={checkoutMetadata ?? { source: 'pricing' }}
            onBeforeCheckout={onBeforeCheckout}
            packageId={creditPackage.id}
            returnTo={returnTo}
            returnUrl={returnUrl}
            size={null}
            variant={null}
          >
            <IconCoins className="size-4" />
            {actionLabel}
          </CreditCheckoutButton>
        ) : (
          <Link to={Routes.Login} className={buttonClassName}>
            <IconCoins className="size-4" />
            Log in to buy
          </Link>
        )}
      </div>
    </article>
  );
}
